/**
 * AiTopicContent — renders the body/tags/related-topics from an AI
 * Topic doc. When the doc isn't ready yet, shows a friendly
 * "🪄 Generating content" banner. When it fails, shows a retry CTA.
 *
 * Injects the JSON-LD structured_data into <head> via a portal-less
 * <script> tag so search engines can index the page.
 *
 * Usage:
 *   const t = useAiTopic({ source, entityKey, title, context, deepLink });
 *   <AiTopicContent {...t} title="Pest Forecast" />
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Loader2, Wand2, RefreshCcw, Tag, Link as LinkIcon, Video, ExternalLink, Newspaper, Mic } from "lucide-react";
import Linkify from "@/components/Linkify";
import SubmitTopicMediaForm from "@/components/SubmitTopicMediaForm";
import RelatedTopicChip from "@/components/RelatedTopicChip";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL || "";
const toAbsUrl = (u) => (!u ? "" : /^https?:\/\//i.test(u) ? u : `${API_ORIGIN}${u.startsWith("/") ? "" : "/"}${u}`);

function parseVideoEmbed(u) {
  if (!u) return null;
  try {
    const url = new URL(u);
    const host = url.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = url.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      const v = url.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}`;
      const m = url.pathname.match(/^\/(shorts|embed|v|live)\/([A-Za-z0-9_-]+)/);
      if (m) return `https://www.youtube.com/embed/${m[2]}`;
    }
    if (host.endsWith("vimeo.com")) {
      const id = url.pathname.split("/").filter(Boolean).pop();
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* fallthrough */ }
  return null;
}

const STATUS_COPY = {
  pending:    "Queued — content will appear here in a moment.",
  generating: "Writing the article now — check back in a few seconds.",
};

export default function AiTopicContent({ doc, status, retry, title, onMediaSubmitted }) {
  // Inject JSON-LD once we have it.
  useEffect(() => {
    if (!doc?.structured_data) return;
    const id = `ai-topic-jsonld-${doc.slug}`;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(doc.structured_data);
    return () => { el?.remove(); };
  }, [doc?.slug, doc?.structured_data]);

  if (status === "ready" && doc?.body) {
    const websites  = [...(doc.website_links      || []), ...(doc.user_website_links || [])];
    const videos    = [...(doc.videos             || []), ...(doc.user_videos        || [])];
    const photos    = doc.photos || [];
    const hero      = doc.hero_image;
    return (
      <div data-testid="ai-topic-content">
        {hero?.url && (
          <div
            className="rounded-2xl overflow-hidden border mb-5"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="ai-topic-hero"
          >
            <img
              src={hero.url}
              alt={hero.caption || title || doc.title}
              loading="lazy"
              className="w-full max-h-80 object-cover"
            />
            {(hero.caption || hero.source) && (
              <div className="px-4 py-2 text-[11px] opacity-70 flex items-center justify-between gap-2">
                <span className="truncate">{hero.caption || ""}</span>
                {hero.source_url && (
                  <a href={hero.source_url} target="_blank" rel="noreferrer" className="underline shrink-0">
                    Source: {hero.source || "wikipedia"}
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* Wikimedia mini gallery — auto-hides when empty. */}
        {Array.isArray(doc.gallery_images) && doc.gallery_images.length > 0 && (
          <div className="mb-5 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="ai-topic-gallery">
            {doc.gallery_images.map((g, i) => (
              <a
                key={i}
                href={g.url}
                target="_blank"
                rel="noreferrer"
                className="block rounded-lg overflow-hidden border aspect-square relative"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`ai-topic-gallery-${i}`}
              >
                <img src={g.url} alt={g.caption || ""} loading="lazy"
                     className="w-full h-full object-cover hover:opacity-90 transition" />
                {g.caption && (
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-2 py-1 truncate">
                    {g.caption}
                  </div>
                )}
              </a>
            ))}
          </div>
        )}

        <div className="wofy-prose text-[15px] leading-relaxed">
          {String(doc.body).split(/\n{2,}/).filter((p) => p.trim()).map((p, i) => (
            <p key={i} className="mb-4"><Linkify text={p.trim()} /></p>
          ))}
        </div>

        {/* User-submitted photos */}
        {photos.length > 0 && (
          <div className="mt-6" data-testid="ai-topic-photos">
            <div className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">Community photos</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photos.map((p, i) => (
                <a
                  key={i}
                  href={toAbsUrl(p.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="block aspect-square rounded-lg overflow-hidden border relative"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`ai-topic-photo-${i}`}
                >
                  <img src={toAbsUrl(p.url)} alt={p.caption || ""} loading="lazy" className="w-full h-full object-cover hover:opacity-90 transition" />
                  {(p.caption || p.user_name) && (
                    <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-2 py-1 truncate">
                      {p.caption ? p.caption : ""}{p.caption && p.user_name ? " · " : ""}{p.user_name ? `by ${p.user_name}` : ""}
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Videos — first one gets an embed, rest stay as compact link cards */}
        {videos.length > 0 && (
          <div className="mt-6" data-testid="ai-topic-videos">
            <div className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">Videos</div>
            {videos.slice(0, 1).map((v, i) => {
              const embed = parseVideoEmbed(v.url);
              return embed ? (
                <div key={i} className="rounded-lg overflow-hidden border aspect-video mb-2" style={{ borderColor: "var(--wofy-border)" }} data-testid={`ai-topic-video-embed-${i}`}>
                  <iframe src={embed} title={v.title || "video"} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
                </div>
              ) : null;
            })}
            <ul className="text-sm space-y-1">
              {videos.map((v, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Video className="w-3.5 h-3.5 mt-1 opacity-60 shrink-0" />
                  <a href={v.url} target="_blank" rel="noreferrer" className="hover:underline break-all" data-testid={`ai-topic-video-link-${i}`}>
                    {v.title || v.url}
                  </a>
                  {v.user_name && <span className="text-[10px] opacity-60 shrink-0 mt-1">· {v.user_name}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Website references */}
        {websites.length > 0 && (
          <div className="mt-6" data-testid="ai-topic-websites">
            <div className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">Website references</div>
            <ul className="text-sm space-y-1.5">
              {websites.map((w, i) => (
                <li key={i} className="flex items-start gap-2">
                  <LinkIcon className="w-3.5 h-3.5 mt-1 opacity-60 shrink-0" />
                  <a href={w.url} target="_blank" rel="noreferrer" className="hover:underline break-all inline-flex items-center gap-1" data-testid={`ai-topic-website-${i}`}>
                    <span>{w.title || w.url}</span>
                    <ExternalLink className="w-3 h-3 opacity-50" />
                  </a>
                  {w.user_name && <span className="text-[10px] opacity-60 shrink-0 mt-1">· added by {w.user_name}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* News links */}
        {(doc.news_links || []).length > 0 && (
          <div className="mt-6" data-testid="ai-topic-news">
            <div className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">News coverage</div>
            <ul className="text-sm space-y-1.5">
              {doc.news_links.map((n, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Newspaper className="w-3.5 h-3.5 mt-1 opacity-60 shrink-0" />
                  <a href={n.url} target="_blank" rel="noreferrer" className="hover:underline break-all inline-flex items-center gap-1" data-testid={`ai-topic-news-${i}`}>
                    <span>{n.title || n.url}</span>
                    <ExternalLink className="w-3 h-3 opacity-50" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Podcasts */}
        {(doc.podcast_links || []).length > 0 && (
          <div className="mt-6" data-testid="ai-topic-podcasts">
            <div className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">Podcasts</div>
            <ul className="text-sm space-y-1.5">
              {doc.podcast_links.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Mic className="w-3.5 h-3.5 mt-1 opacity-60 shrink-0" />
                  <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline break-all inline-flex items-center gap-1" data-testid={`ai-topic-podcast-${i}`}>
                    <span>{p.title || p.url}</span>
                    <ExternalLink className="w-3 h-3 opacity-50" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {Array.isArray(doc.tags) && doc.tags.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-1.5" data-testid="ai-topic-tags">
            {doc.tags.map((t) => (
              <Link
                key={t}
                to={`/ai-topics?q=${encodeURIComponent(t)}`}
                className="inline-flex items-center gap-1 text-xs rounded-full border px-2.5 py-1 hover:bg-black/[0.04] transition"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`ai-topic-tag-${t}`}
              >
                <Tag className="w-3 h-3 opacity-60" /> {t}
              </Link>
            ))}
          </div>
        )}

        {Array.isArray(doc.related_topics) && doc.related_topics.length > 0 && (
          <div className="mt-6" data-testid="ai-topic-related">
            <div className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">Related topics</div>
            <div className="flex flex-wrap gap-2">
              {doc.related_topics.map((r) => (
                <RelatedTopicChip key={r} label={r} />
              ))}
            </div>
          </div>
        )}

        {/* Contribute form — always visible; nudges guests to sign in. */}
        <SubmitTopicMediaForm slug={doc.slug} onSubmitted={onMediaSubmitted} />

        <p className="mt-8 text-[11px] opacity-50">
          Written by Social Pulse's community knowledge engine · Neutral, AI-assisted
        </p>
      </div>
    );
  }

  if (status === "failed") {
    const isBudget = doc?.error_kind === "budget"
      || /spend limit|quota|budget|rate limit|429|too many requests/i.test(doc?.error || "");
    const retryHint = (() => {
      const s = Number(doc?.retry_after) || 0;
      if (!s || s < 60) return null;
      const hours = Math.round(s / 3600);
      if (hours >= 2) return `Try again in about ${hours} hours.`;
      return "Try again in about an hour.";
    })();

    return (
      <div
        className="rounded-2xl border p-5 flex items-start gap-3"
        style={{
          borderColor: isBudget ? "#F59E0B44" : "var(--wofy-border)",
          background:  isBudget ? "linear-gradient(135deg,#FEF3C7,#FDE68A)" : "var(--wofy-surface)",
          color:       isBudget ? "#7C2D12" : undefined,
        }}
        data-testid="ai-topic-failed"
      >
        <div className="flex-1">
          {isBudget ? (
            <>
              <div className="font-medium">
                ✨ AI writer is resting — this section will publish itself shortly.
              </div>
              <div className="text-xs opacity-80 mt-1">
                {retryHint || "The daily writing budget just refilled and every location page is queued. Check back in a few hours."}
              </div>
            </>
          ) : (
            <>
              <div className="font-medium">We couldn't generate content for {title} just now.</div>
              <div className="text-xs opacity-70 mt-1">
                {doc?.error || "The AI writer hit a snag. Please try again in a moment."}
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={retry}
          className="wofy-btn-ghost text-sm inline-flex items-center gap-1"
          data-testid="ai-topic-retry"
        >
          <RefreshCcw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  // pending / generating / idle / missing — show the "cooking" banner.
  return (
    <div
      className="rounded-2xl border p-5 flex items-start gap-3"
      style={{
        borderColor: "var(--wofy-border)",
        background:  "linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)",
        color:       "#7C2D12",
      }}
      data-testid="ai-topic-generating"
    >
      <Wand2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <div className="font-medium">🪄 Generating this topic page — check back in a minute</div>
        <div className="text-xs mt-1 opacity-80">
          {STATUS_COPY[status] || "Preparing content…"} We'll auto-refresh once it's ready.
        </div>
      </div>
      <Loader2 className="w-4 h-4 animate-spin mt-1 opacity-70" />
    </div>
  );
}
